import React, { FC, useState } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useAuthProfile } from '../../app/hooks';
import { useGetChartSummaryStreamQuery } from './healthWalletSlice';
import type { ChartSummaryStreamData } from './healthWalletSlice';
import { BRAND_DARKER, AI_SUMMARY_DISCLAIMER } from './healthWalletConstants';
import { V1_ACCENT, V1_ACCENT_TINT } from './RendererV1/theme';

// ============================================================================
// Shared AI chart UI (Trends + Observations) over a REAL SSE stream, backed by an
// RTK Query STREAMING endpoint (getChartSummaryStream + onCacheEntryAdded). The
// result lives in the RTK cache keyed per-arg {resource,name,type,profileId}, so a
// measure can NEVER inherit another's entry — fixing the wrong-summary bug the old
// hand-rolled module Map caused. `type` ROUTES which section a request produces:
//   'summary'   → the summary box (compact = Observations, full = Trends)
//   'questions' → the "Questions for your doctor" accordion (Trends only), fired as
//                 its OWN request when the patient expands it.
// The hook below is a thin wrapper; presentational components render its pieces.
// ============================================================================

const SEED_STATUS = 'Analyzing your trend…';
const IDLE: ChartSummaryStreamData = { status: null, summary: null, questions: null, loading: false };

/**
 * Thin wrapper over the RTK streaming endpoint. `enabled` gates it (Settings AI toggle, and — for the
 * questions accordion — whether it's expanded), so nothing streams until it's actually wanted. RTK keys the
 * cache by the whole arg, so each measure/type gets its own entry (no cross-measure staleness), and the
 * result is shared/deduped across every consumer of the same measure.
 */
export function useChartSummaryStream(
  resource: string,
  name: string,
  enabled: boolean,
  type: 'summary' | 'questions' = 'summary',
): ChartSummaryStreamData {
  const { profile } = useAuthProfile();
  const profileId = profile?.id?.toString() ?? '';
  const { data } = useGetChartSummaryStreamQuery(
    { resource, name, type, profileId },
    { skip: !enabled || !name || !profileId },
  );
  return data ?? IDLE;
}

const Loading: FC<{ status: string | null; variant: 'compact' | 'full' }> = ({ status, variant }) =>
  variant === 'compact' ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
      <CircularProgress size={12} sx={{ color: BRAND_DARKER }} />
      <Typography variant="caption" color="text.secondary">{status ?? SEED_STATUS}</Typography>
    </Box>
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1.5 }}>
      <CircularProgress size={18} sx={{ color: V1_ACCENT }} />
      <Typography sx={{ fontSize: 13, color: '#6b6b70' }}>{status ?? SEED_STATUS}</Typography>
    </Box>
  );

/**
 * The summary box. `compact` = the Observations ValueChart "AI Insight"; `full` = the Trends "What this
 * may mean". Shows the live status line while generating, then the summary; nothing on soft-fail.
 */
export const ChartAiSummary: FC<{ variant: 'compact' | 'full'; status: string | null; summary: string | null; loading: boolean }> = ({
  variant,
  status,
  summary,
  loading,
}) => {
  if (!summary && !loading) return null;

  if (variant === 'compact') {
    return (
      <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#efeaff', borderRadius: 2, border: '1px solid #d7d3f0' }} aria-live="polite">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <AutoAwesomeIcon sx={{ fontSize: 14, color: BRAND_DARKER }} aria-hidden="true" />
          <Typography variant="caption" fontWeight={700} sx={{ color: BRAND_DARKER, fontSize: '0.7rem' }}>AI Insight</Typography>
        </Box>
        {summary ? (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5, display: 'block' }}>{summary}</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, fontSize: '0.6rem', color: 'text.disabled', fontStyle: 'italic' }}>{AI_SUMMARY_DISCLAIMER}</Typography>
          </>
        ) : <Loading status={status} variant="compact" />}
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, bgcolor: V1_ACCENT_TINT }} aria-live="polite">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <AutoAwesomeIcon sx={{ fontSize: 15, color: V1_ACCENT }} aria-hidden="true" />
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: V1_ACCENT, textTransform: 'uppercase' }}>What this may mean</Typography>
      </Box>
      {summary ? (
        <>
          <Typography sx={{ fontSize: '14px', lineHeight: 1.55, color: '#2a2a2c' }}>{summary}</Typography>
          <Typography sx={{ display: 'block', mt: 1.25, fontSize: '11px', color: '#8e8e93', fontStyle: 'italic', lineHeight: 1.5 }}>{AI_SUMMARY_DISCLAIMER}</Typography>
        </>
      ) : <Loading status={status} variant="full" />}
    </Paper>
  );
};

/**
 * "Questions for your doctor" — a collapsed accordion (Trends detail only). Expanding fires its OWN lazy
 * `type:'questions'` stream, so nothing is generated until the patient asks for it. While it streams it
 * shows the SAME live SSE progress as the summary; then the questions + disclaimer; and an honest empty
 * note if none. Fed from the same RTK streaming endpoint (type='questions').
 */
export const DoctorQuestionsAccordion: FC<{ resource: string; name: string; enabled: boolean }> = ({ resource, name, enabled }) => {
  const [expanded, setExpanded] = useState(false);
  const { status, questions, loading } = useChartSummaryStream(resource, name, enabled && expanded, 'questions');
  if (!enabled) return null; // AI off → no accordion at all

  const hasQuestions = !!questions && questions.length > 0;
  return (
    <Paper elevation={0} sx={{ borderRadius: '16px', mb: 2, bgcolor: V1_ACCENT_TINT, overflow: 'hidden' }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((v) => !v); } }}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: '16px 20px', cursor: 'pointer', userSelect: 'none' }}
      >
        <HelpOutlineIcon sx={{ fontSize: 15, color: V1_ACCENT }} aria-hidden="true" />
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: V1_ACCENT, textTransform: 'uppercase', flex: 1 }}>Questions for your doctor</Typography>
        <ExpandMoreIcon sx={{ color: V1_ACCENT, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} aria-hidden="true" />
      </Box>
      {expanded && (
        <Box sx={{ px: '20px', pb: '18px' }} aria-live="polite">
          {hasQuestions ? (
            <>
              <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {questions!.map((q, i) => (
                  <Typography component="li" key={i} sx={{ fontSize: '14px', lineHeight: 1.5, color: '#2a2a2c' }}>{q}</Typography>
                ))}
              </Box>
              <Typography sx={{ display: 'block', mt: 1.25, fontSize: '11px', color: '#8e8e93', fontStyle: 'italic', lineHeight: 1.5 }}>{AI_SUMMARY_DISCLAIMER}</Typography>
            </>
          ) : loading ? (
            <Loading status={status} variant="full" />
          ) : (
            <Typography sx={{ fontSize: 13, color: '#8e8e93' }}>No suggested questions for this measure.</Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};
